import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddedIcon,
  Remove as RemovedIcon,
  Edit as DefinitionChangedIcon,
  Rule as RubricChangedIcon,
  Balance as WeightChangedIcon,
  TrackChanges as ObjectiveChangedIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import LogoLoader from "../../common/LogoLoader";
import {
  CriterionChangeType,
  SpecVersionWithDiff,
  researchApi,
} from "../../../services/researchApi";

interface VersionHistoryModalProps {
  open: boolean;
  onClose: () => void;
  trackId: string;
}

const CHANGE_TYPE_META: Record<
  CriterionChangeType,
  {
    label: string;
    color: "success" | "error" | "warning" | "info";
    Icon: React.ElementType;
  }
> = {
  added: { label: "Criterion added", color: "success", Icon: AddedIcon },
  removed: { label: "Criterion removed", color: "error", Icon: RemovedIcon },
  weight_changed: {
    label: "Criterion weight changed",
    color: "info",
    Icon: WeightChangedIcon,
  },
  definition_changed: {
    label: "Criterion definition changed",
    color: "warning",
    Icon: DefinitionChangedIcon,
  },
  rubric_changed: {
    label: "Criterion rubric changed",
    color: "warning",
    Icon: RubricChangedIcon,
  },
};

const formatCountLabel = (
  changeType: CriterionChangeType,
  count: number,
): string => {
  const isSingular = count === 1;
  switch (changeType) {
    case "added":
      return `${count} ${isSingular ? "criterion" : "criteria"} added`;
    case "removed":
      return `${count} ${isSingular ? "criterion" : "criteria"} removed`;
    case "definition_changed":
      return `${count} criterion ${isSingular ? "definition" : "definitions"} changed`;
    case "rubric_changed":
      return `${count} criterion ${isSingular ? "rubric" : "rubrics"} changed`;
    case "weight_changed":
      return `${count} criterion ${isSingular ? "weight" : "weights"} changed`;
  }
};

const SCORE_LABELS: Record<string, string> = {
  "1": "Very Poor",
  "2": "Poor",
  "3": "Adequate",
  "4": "Good",
  "5": "Excellent",
};

const formatTimestamp = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({
  open,
  onClose,
  trackId,
}) => {
  const [versions, setVersions] = useState<SpecVersionWithDiff[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !trackId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await researchApi.getTrackSpecVersions(trackId);
        if (cancelled) return;
        setVersions(data);
        setSelectedId(data.length > 0 ? data[0].id : null);
      } catch (exc: any) {
        if (cancelled) return;
        setError(
          exc?.response?.data?.detail ??
            exc?.message ??
            "Failed to load version history",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, trackId]);

  const selectedVersion = useMemo(
    () => versions.find((v) => v.id === selectedId) ?? null,
    [versions, selectedId],
  );

  const renderChangeSummaryChips = (version: SpecVersionWithDiff) => {
    const counts: Record<CriterionChangeType, number> = {
      added: 0,
      removed: 0,
      weight_changed: 0,
      definition_changed: 0,
      rubric_changed: 0,
    };
    for (const c of version.changes_since_prev.criterion_changes) {
      counts[c.change_type] += 1;
    }
    const items: React.ReactNode[] = [];
    (Object.keys(counts) as CriterionChangeType[]).forEach((key) => {
      const count = counts[key];
      if (count === 0) return;
      const meta = CHANGE_TYPE_META[key];
      items.push(
        <Chip
          key={key}
          size="small"
          color={meta.color}
          variant="outlined"
          icon={<meta.Icon sx={{ fontSize: 14 }} />}
          label={formatCountLabel(key, count)}
          sx={{ "& .MuiChip-icon": { ml: "6px" } }}
        />,
      );
    });
    if (version.changes_since_prev.objective_changed) {
      items.unshift(
        <Chip
          key="objective"
          size="small"
          color="primary"
          variant="outlined"
          icon={<ObjectiveChangedIcon sx={{ fontSize: 14 }} />}
          label="Objective modified"
          sx={{ "& .MuiChip-icon": { ml: "6px" } }}
        />,
      );
    }
    return items;
  };

  const renderVersionDetail = (version: SpecVersionWithDiff) => {
    const isFirst =
      versions.length > 0 && version.id === versions[versions.length - 1].id;
    const diff = version.changes_since_prev;
    const changes = isFirst
      ? version.criteria.map((c) => ({
          change_type: "added" as const,
          criterion_id: c.id,
          label: c.label,
        }))
      : diff.criterion_changes;
    const objectiveChanged = isFirst || diff.objective_changed;

    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Version {version.version_number}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp(version.created_at)} by{" "}
            {version.created_by_username || "unknown"}
          </Typography>
        </Box>

        {!isFirst && changes.length === 0 && !diff.objective_changed && (
          <Alert severity="success" sx={{ mb: 2 }}>
            No criterion changes relative to the previous version.
          </Alert>
        )}

        {(objectiveChanged || changes.length > 0) && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {objectiveChanged && (
              <Paper
                variant="outlined"
                sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    border: 1,
                    borderColor: "primary.main",
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <ObjectiveChangedIcon sx={{ fontSize: 15 }} />
                </Box>
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    component="span"
                    variant="body2"
                    color="primary.main"
                    fontWeight={600}
                    sx={{ mr: 0.5 }}
                  >
                    Objective modified
                  </Typography>
                </Typography>
              </Paper>
            )}
            {changes.map((change) => {
              const meta = CHANGE_TYPE_META[change.change_type];
              return (
                <Paper
                  key={`${change.change_type}-${change.criterion_id}`}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      border: 1,
                      borderColor: `${meta.color}.main`,
                      color: `${meta.color}.main`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <meta.Icon sx={{ fontSize: 15 }} />
                  </Box>
                  <Typography variant="body2" noWrap title={change.label} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      component="span"
                      variant="body2"
                      color={`${meta.color}.main`}
                      fontWeight={600}
                      sx={{ mr: 0.5 }}
                    >
                      {meta.label}:
                    </Typography>
                    {change.label || change.criterion_id}
                  </Typography>
                </Paper>
              );
            })}
          </Stack>
        )}

        <Typography variant="h6" fontWeight={600} gutterBottom>
          Objective
        </Typography>
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", mb: 3 }}
          color="text.primary"
        >
          {version.spec_payload.objective || (
            <em style={{ color: "rgba(255,255,255,0.5)" }}>No objective</em>
          )}
        </Typography>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Criteria ({version.criteria.length})
        </Typography>
        <Stack spacing={2} divider={<Divider flexItem />}>
          {version.criteria.map((c) => {
            const rubricAnchors =
              (
                c.criterion_payload?.rubric as
                  | { anchors?: Record<string, string> }
                  | undefined
              )?.anchors ?? {};
            return (
              <Box key={c.id}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body1" fontWeight={600}>
                    {c.label}
                  </Typography>
                  <Chip
                    size="small"
                    label={`Weight ${c.weight}/5`}
                    sx={{ ml: "auto", fontWeight: 600 }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {c.criterion_payload.description || "No description"}
                </Typography>

                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    mt: 1,
                    backgroundColor: "transparent",
                    border: 1,
                    borderColor: "divider",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ minHeight: 36, px: 1.5 }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      Scoring Rubric
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {["5", "4", "3", "2", "1"].map((scoreKey) => {
                        const anchorText = rubricAnchors[scoreKey]?.trim();
                        return (
                          <Box
                            key={scoreKey}
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "flex-start",
                            }}
                          >
                            <Chip
                              label={scoreKey}
                              size="small"
                              sx={{
                                mt: 0.25,
                                minWidth: 28,
                                fontWeight: 600,
                                backgroundColor:
                                  scoreKey === "5"
                                    ? "success.dark"
                                    : scoreKey === "4"
                                      ? "success.main"
                                      : scoreKey === "3"
                                        ? "warning.main"
                                        : scoreKey === "2"
                                          ? "error.main"
                                          : "error.dark",
                                color: "white",
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                lineHeight: 1.5,
                                color: anchorText
                                  ? "text.secondary"
                                  : "text.disabled",
                                fontStyle: anchorText ? "normal" : "italic",
                              }}
                            >
                              {anchorText ||
                                `${SCORE_LABELS[scoreKey]} (not defined)`}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            );
          })}
        </Stack>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#1e1e1e" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#1e1e1e",
        }}
      >
        Requirements Version History
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ p: 0, height: "70vh", backgroundColor: "#1e1e1e" }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <LogoLoader size="section" surface="paper" label="Loading the history" />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : versions.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">No saved requirements versions yet.</Alert>
          </Box>
        ) : (
          <Box sx={{ display: "flex", height: "100%" }}>
            <Box
              sx={{
                width: 320,
                borderRight: 1,
                borderColor: "divider",
                overflowY: "auto",
              }}
            >
              <List disablePadding>
                {versions.map((v) => {
                  const summaryChips = renderChangeSummaryChips(v);
                  return (
                    <React.Fragment key={v.id}>
                      <ListItemButton
                        selected={selectedId === v.id}
                        onClick={() => setSelectedId(v.id)}
                        sx={{ py: 1.5 }}
                      >
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2" fontWeight={600}>
                                Version {v.version_number}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {formatTimestamp(v.created_at)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mb: 0.5 }}
                              >
                                {v.created_by_username || "unknown"}
                              </Typography>
                              {summaryChips.length > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {summaryChips}
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                      <Divider />
                    </React.Fragment>
                  );
                })}
              </List>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto", px: 8, py: 3 }}>
              {selectedVersion ? (
                renderVersionDetail(selectedVersion)
              ) : (
                <Typography color="text.secondary">
                  Select a version to see details.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VersionHistoryModal;
